export class RestAlertService {
  private context: AudioContext | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private activeDeadline: number | null = null;
  private firedDeadline: number | null = null;
  private nodes: OscillatorNode[] = [];

  unlock(): void {
    const context = this.getContext();
    if (!context) return;
    if (context.state === 'suspended') {
      void context.resume().catch(() => undefined);
    }
  }

  schedule(deadline: number): void {
    const context = this.getContext();
    if (!context) return;

    this.cancel();
    this.firedDeadline = null;
    if (context.state === 'suspended') {
      void context.resume().catch(() => undefined);
    }

    this.activeDeadline = deadline;
    const delay = Math.max(0, deadline - Date.now());
    this.timer = setTimeout(() => {
      this.timer = null;
      if (this.activeDeadline !== deadline || this.firedDeadline === deadline) return;
      this.firedDeadline = deadline;
      this.beep();
    }, delay);
  }

  cancel(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.activeDeadline = null;
    for (const node of this.nodes) {
      try {
        node.stop();
      } catch {
        void 0;
      }
    }
    this.nodes = [];
  }

  playIfMissed(deadline: number): void {
    if (deadline > Date.now()) return;
    if (this.firedDeadline === deadline) return;

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.activeDeadline === deadline) this.activeDeadline = null;
    this.firedDeadline = deadline;
    this.unlock();
    this.beep();
  }

  private getContext(): AudioContext | null {
    if (this.context) return this.context;
    if (typeof window === 'undefined') return null;

    const AudioContextConstructor =
      window.AudioContext ??
      (window as Window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioContextConstructor) return null;

    try {
      this.context = new AudioContextConstructor();
    } catch {
      return null;
    }
    return this.context;
  }

  private beep(): void {
    const context = this.getContext();
    if (!context) return;
    if (context.state === 'suspended') {
      void context.resume().catch(() => undefined);
    }

    const start = context.currentTime;
    for (let i = 0; i < 3; i += 1) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const at = start + i * 0.25;

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, at);
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.25, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.18);

      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(at);
      oscillator.stop(at + 0.2);
      this.nodes.push(oscillator);
      oscillator.onended = () => {
        this.nodes = this.nodes.filter((node) => node !== oscillator);
      };
    }
  }
}

export const restAlert = new RestAlertService();
